import React from 'react';
import { Image, MaskImage } from '@/pages/reports/components/ui';
import SmileRevertColored from '@/assets/icons/SmileRevertColored.svg';
import SmileWhite from '@/assets/icons/SmileWhite.svg';
import Arrow from '@/assets/images/Arrow.svg?url';
import Live from '@/assets/images/Live.svg?url';
import { resolveHeroBackgroundColor, resolveHeroTextColor } from '@/pages/reports/utils/heroColors';
import { useReportI18n } from '@/pages/reports/i18n';

export interface HeroLogoLink {
  imageUrl: string;
  link?: string;
}

export interface HeroProps {
  date: string;
  edition: string | number;
  topCenterImage?: string;
  topRightImage: string;
  bottomLeftImage: string;
  backgroundColor?: string;
  textColor?: string;
  logoLinks?: HeroLogoLink[];
}

export const HeroDesktop: React.FC<HeroProps> = ({
  date,
  edition,
  topCenterImage,
  topRightImage,
  bottomLeftImage,
  backgroundColor,
  textColor,
  logoLinks = [],
}) => {
  const { t, showLatamSeal } = useReportI18n();
  const resolvedBackgroundColor = resolveHeroBackgroundColor(backgroundColor);
  const resolvedTextColor = resolveHeroTextColor(textColor);

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: resolvedBackgroundColor }}
    >
      <div className="container mx-auto relative pt-4">
        {topCenterImage && (
          <div className="flex justify-center mb-6 relative z-10">
            <Image
              src={topCenterImage}
              alt=""
              className="h-auto w-auto max-w-full"
            />
          </div>
        )}
        <div className="grid grid-rows-[auto_auto_auto] gap-4 md:gap-6 relative z-10">
          <div className="flex items-center gap-2 md:gap-3">
            <h1
              className="font-normal text-[153.51px] flex-1 leading-[137.52px] tracking-[-0.02em] font-['Times_New_Roman']"
              style={{ color: resolvedTextColor }}
            >
              Culture
            </h1>
            <img
              src={SmileRevertColored}
              alt=""
              className="w-[120px] mt-1 md:mt-2 shrink-1"
            />
            <div className="flex flex-1 justify-center">
              <Image
                src={topRightImage}
                alt=""
                className="h-auto w-auto max-w-full shrink-0 rounded-sm"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 w-full justify-between">
            <div className="flex flex-1 gap-1">
              <MaskImage src={Arrow} color={resolvedTextColor} className="w-36 h-12 shrink-0" ariaHidden />
              <MaskImage src={Arrow} color={resolvedTextColor} className="w-36 h-12 shrink-0" ariaHidden />
              <MaskImage src={Arrow} color={resolvedTextColor} className="w-36 h-12 shrink-0" ariaHidden />
            </div>
            <h1
              className="font-normal text-[153.51px] leading-[137.52px] tracking-[-0.02em] font-['Times_New_Roman']"
              style={{ color: resolvedTextColor }}
            >
              Monitor
            </h1>
          </div>

          <div className="flex gap-2 md:gap-3 items-end">
            <div className="flex justify-center">
              <Image
                src={bottomLeftImage}
                alt=""
                className="h-auto w-auto max-w-full rounded-sm"
              />
            </div>
            <div className="flex flex-col gap-2 justify-between pt-3 pb-10 px-8">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="flex flex-col items-start">
                  <MaskImage
                    src={Live}
                    color={resolvedTextColor}
                    className="w-[320px] h-28"
                    ariaLabel="Live"
                  />
                  {showLatamSeal && (
                    <span
                      className="font-['Times_New_Roman'] text-[28px] leading-none tracking-[0.18em] mt-1 whitespace-nowrap"
                      style={{ color: resolvedTextColor }}
                    >
                      {t('hero.latam')}
                    </span>
                  )}
                </div>
                <img
                  src={SmileWhite}
                  alt=""
                  className="w-[120px]"
                />
              </div>
              <div
                className="text-sm md:text-base lg:text-lg"
                style={{ color: resolvedTextColor }}
              >
                {date} - {t('hero.edition')}: {edition}
              </div>
              {logoLinks.length > 0 && (
                <div className="flex flex-wrap items-center justify-start gap-2 mt-4">
                  {logoLinks.map((item, index) => {
                    const content = (
                      <Image
                        key={index}
                        src={item.imageUrl}
                        alt=""
                        className="h-auto w-auto max-w-full"
                      />
                    );
                    return item.link ? (
                      <a
                        key={index}
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center"
                      >
                        {content}
                      </a>
                    ) : (
                      <span key={index} className="flex items-center">
                        {content}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
